import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const useBootstrap = (fn) => {
  useEffect(() => {
    fn();
  }, [fn]);
};

const Newsletter = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}/newsletter`);
      setIssues(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useBootstrap(load);

  return (
    <main data-testid="newsletter-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO title="Newsletter" description="Past issues from Vaymilo — calm notes on digital tools, AI workflows, and shipping work that matters." path="/newsletter" />
      <PromoBanner />
      <Header />

      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <div className="overline">— The Vaymilo Newsletter</div>
          <h1 className="mt-5 font-sora text-[40px] sm:text-[54px] lg:text-[64px] font-extrabold tracking-tight leading-[1.05] text-[#111111]">
            Calm notes on shipping.
          </h1>
          <p className="mt-5 max-w-xl font-manrope text-[16px] md:text-[17px] text-[#111111]/65">
            Occasional, short, and useful. New kits, behind-the-scenes builds, and small ideas worth stealing.
          </p>
          <Link
            to="/start"
            data-testid="newsletter-cta-start"
            className="mt-7 inline-flex items-center gap-2 bg-[#111111] hover:bg-[#2563EB] text-white font-manrope font-medium text-sm px-5 py-3 rounded-lg transition-colors"
          >
            Subscribe via the quiz
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <section className="flex-1 bg-white border-b border-[#E7E7E2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline">— Archive</div>

          {loading && (
            <div data-testid="newsletter-loading" className="mt-10 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-[#F7F7F4] border border-[#E7E7E2] rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {!loading && error && (
            <p data-testid="newsletter-error" className="mt-8 font-manrope text-[15px] text-[#111111]/60">
              Couldn&rsquo;t load issues. Refresh the page or come back later.
            </p>
          )}

          {!loading && !error && issues.length === 0 && (
            <div data-testid="newsletter-empty" className="mt-10 bg-[#F7F7F4] border border-[#E7E7E2] rounded-2xl p-10 text-center">
              <p className="font-sora text-[22px] font-semibold text-[#111111]">No issues yet.</p>
              <p className="mt-3 font-manrope text-[14px] text-[#111111]/65 max-w-md mx-auto">
                The first issue lands when we launch. Join the list now so you don&rsquo;t miss it.
              </p>
              <Link
                to="/start"
                className="mt-6 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-sm px-5 py-3 rounded-lg transition-colors"
              >
                Join via the quiz <ArrowUpRight size={14} />
              </Link>
            </div>
          )}

          {!loading && !error && issues.length > 0 && (
            <ul data-testid="newsletter-list" className="mt-10 divide-y divide-[#E7E7E2] border-t border-b border-[#E7E7E2]">
              {issues.map((it, idx) => (
                <li key={it.id}>
                  <Link
                    to={`/newsletter/${it.slug}`}
                    data-testid={`newsletter-item-${it.slug}`}
                    className="group block py-8 md:py-10 flex flex-col md:flex-row md:items-baseline gap-3 md:gap-10 hover:bg-[#F7F7F4]/60 transition-colors -mx-4 px-4 rounded-lg"
                  >
                    <div className="md:w-32 shrink-0 font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
                      <span className="mr-3 text-[#2563EB]">{String(idx + 1).padStart(2, "0")}</span>
                      {fmtDate(it.published_at || it.created_at)}
                    </div>
                    <div className="flex-1">
                      <h2 className="font-sora text-[22px] md:text-[26px] font-bold text-[#111111] tracking-tight group-hover:text-[#2563EB] transition-colors">
                        {it.title}
                      </h2>
                      <p className="mt-2 font-manrope text-[15px] text-[#111111]/70 leading-relaxed line-clamp-2">
                        {it.excerpt}
                      </p>
                    </div>
                    <ArrowUpRight size={18} className="text-[#111111]/30 group-hover:text-[#2563EB] transition-colors shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Newsletter;
